'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'
import type { ReactNode } from 'react'

export function BrowserFrame({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={clsx('overflow-hidden rounded-[var(--radius-panel)] bg-white shadow-card dark:bg-mist-900', className)}
    data-sentry-component="BrowserFrame" data-sentry-source-file="frames.tsx">
      <div className="flex items-center gap-1.5 border-b border-mist-950/5 px-4 py-3 dark:border-white/10">
        <span className="size-2.5 rounded-full bg-mist-300 dark:bg-mist-600" />
        <span className="size-2.5 rounded-full bg-mist-300 dark:bg-mist-600" />
        <span className="size-2.5 rounded-full bg-mist-300 dark:bg-mist-600" />
      </div>
      <div>{children}</div>
    </div>
  )
}

/**
 * The iOS system status bar: clock left, Dynamic Island centred, radio/battery indicators right.
 *
 * It is what makes the chassis read as a phone rather than as a rounded rectangle around a
 * screenshot — the island alone left the top strip empty and ambiguous. The two flanks are given
 * an equal fixed width so the island stays optically centred regardless of how wide the clock or
 * the indicators render.
 *
 * 9:41 is deliberate: it is the time Apple ships in every device mock, so it reads as "a phone"
 * and never as stale data the way a real-looking arbitrary time would.
 */
function StatusBar() {
  return (
    <div className="flex shrink-0 items-center justify-between px-5 pt-[6px] pb-[3px] text-mist-950 dark:text-white" data-sentry-component="StatusBar" data-sentry-source-file="frames.tsx">
      <span className="w-11 text-[8px] leading-none font-semibold tabular-nums">9:41</span>
      <div className="h-3 w-[46px] shrink-0 rounded-full bg-mist-950 dark:bg-black" />
      <span className="flex w-11 items-center justify-end gap-[3px]" aria-hidden="true">
        {/* Signal strength: four ascending bars. */}
        <svg viewBox="0 0 17 12" width="10" fill="currentColor" data-sentry-element="svg" data-sentry-source-file="frames.tsx">
          <rect x="0" y="8" width="3" height="4" rx="1" data-sentry-element="rect" data-sentry-source-file="frames.tsx" />
          <rect x="4.6" y="5.8" width="3" height="6.2" rx="1" data-sentry-element="rect" data-sentry-source-file="frames.tsx" />
          <rect x="9.2" y="3.2" width="3" height="8.8" rx="1" data-sentry-element="rect" data-sentry-source-file="frames.tsx" />
          <rect x="13.8" y="0" width="3" height="12" rx="1" data-sentry-element="rect" data-sentry-source-file="frames.tsx" />
        </svg>
        {/* Wi-Fi: two arcs over a dot. */}
        <svg viewBox="0 0 16 12" width="9.5" fill="none" stroke="currentColor" strokeLinecap="round" data-sentry-element="svg" data-sentry-source-file="frames.tsx">
          <path d="M1.4 4.6a9.5 9.5 0 0 1 13.2 0" strokeWidth="1.6" data-sentry-element="path" data-sentry-source-file="frames.tsx" />
          <path d="M4.2 7.4a5.5 5.5 0 0 1 7.6 0" strokeWidth="1.6" data-sentry-element="path" data-sentry-source-file="frames.tsx" />
          <circle cx="8" cy="10.2" r="1.15" fill="currentColor" stroke="none" data-sentry-element="circle" data-sentry-source-file="frames.tsx" />
        </svg>
        {/* Battery: outline, charge level, contact nub. */}
        <svg viewBox="0 0 25 12" width="13.5" data-sentry-element="svg" data-sentry-source-file="frames.tsx">
          <rect
            x="0.6"
            y="0.6"
            width="20.8"
            height="10.8"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
            opacity="0.35"
          data-sentry-element="rect" data-sentry-source-file="frames.tsx" />
          <rect x="2.2" y="2.2" width="14.5" height="7.6" rx="1.7" fill="currentColor" data-sentry-element="rect" data-sentry-source-file="frames.tsx" />
          <rect x="22.7" y="4" width="1.8" height="4" rx="0.9" fill="currentColor" opacity="0.35" data-sentry-element="rect" data-sentry-source-file="frames.tsx" />
        </svg>
      </span>
    </div>
  )
}

/**
 * Phone chassis with a fixed, believable size (~270x585px, a 9:19.5 aspect ratio matching the
 * source design) so it never collapses — callers should NOT override width/height via
 * `className`; pass only non-sizing classes there.
 *
 * The status bar (and with it the Dynamic Island) sits in normal flow, in its own row above
 * `children` (as in the source design), rather than absolutely overlaid on top of the screen
 * content — that overlay approach is what previously caused the island to collide with the
 * in-app status bar.
 */
export function PhoneFrame({
  children,
  className,
  screenClassName,
}: {
  children: ReactNode
  className?: string
  /**
   * Applied to the screen itself. Pass the app's own surface class here: the strip around the
   * Dynamic Island is part of the screen, so if the frame keeps its own background that strip ends
   * up a different colour from the app right below it.
   */
  screenClassName?: string
}) {
  return (
    <div
      className={clsx(
        'relative mx-auto h-[585px] w-[270px] shrink-0 rounded-[34px] bg-mist-950 p-[5px] shadow-panel',
        // In dark mode the page itself is near-black, so a black chassis and a black drop shadow
        // both disappear into it. Lift the body above the page and add the same hairline rim the
        // dropdowns and dialogs use, so the device still reads as an object.
        'dark:bg-mist-800 dark:ring-1 dark:ring-white/10',
        className,
      )}
    data-sentry-component="PhoneFrame" data-sentry-source-file="frames.tsx">
      {/*
        Concentric with the chassis: the 34px outer radius minus the 5px bezel leaves a 29px hole,
        so the screen must round at 29px too. At a tighter radius the screen cuts its own corner
        across the chassis curve and the bezel visibly fattens at the four corners.
      */}
      <div
        className={clsx(
          'flex h-full flex-col overflow-hidden rounded-[29px]',
          screenClassName ?? 'bg-white dark:bg-mist-900',
        )}
      >
        <StatusBar data-sentry-element="StatusBar" data-sentry-source-file="frames.tsx" />
        <div className="min-h-0 flex-1">{children}</div>
      </div>
    </div>
  )
}
